import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Bus, UserCircle2, Map, Users, Wrench, Coins, Calendar, CheckCircle2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend } from "recharts";
import { useSchools, SchoolSelect } from "../../components/SchoolSelect";

function Stat({ icon: Icon, label, value }) {
  return (
    <Card className="p-5 border shadow-none card-hover smooth">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className="text-3xl font-bold mt-2 text-primary">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center"><Icon className="w-5 h-5" /></div>
      </div>
    </Card>
  );
}

export default function TransportReportsPage() {
  const { t } = useI18n();
  const { schools, schoolId, setSchoolId } = useSchools();
  const [r, setR] = useState(null);
  const [charts, setCharts] = useState(null);

  useEffect(() => {
    if (!schoolId) return;
    api.get(`/transport/report?school_id=${schoolId}`).then(rs => setR(rs.data)).catch(() => {});
    api.get(`/transport/charts?school_id=${schoolId}`).then(rs => setCharts(rs.data)).catch(() => {});
  }, [schoolId]);

  if (!r) return <div className="p-8 text-muted-foreground">...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><Map className="w-7 h-7 text-primary" /> {t("transport_reports")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("transport")}</p>
        </div>
        <SchoolSelect schools={schools} value={schoolId} onChange={setSchoolId} testId="transport-reports-school-select" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Bus} label={t("vehicles")} value={`${r.vehicles_active}/${r.vehicles}`} />
        <Stat icon={UserCircle2} label={t("drivers")} value={`${r.drivers_active}/${r.drivers}`} />
        <Stat icon={Map} label={t("routes")} value={`${r.routes_active}/${r.routes}`} />
        <Stat icon={Users} label={t("students_enrolled")} value={r.students_enrolled} />
        <Stat icon={Wrench} label={t("maintenance")} value={r.maintenance_records} />
        <Stat icon={Coins} label={t("total_cost") + " (SAR)"} value={Number(r.total_maintenance_cost || 0).toLocaleString()} />
        <Stat icon={Calendar} label={t("trips") + " · " + t("today")} value={r.trips_today} />
        <Stat icon={CheckCircle2} label={t("completed")} value={r.trips_completed_today} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 border shadow-none" data-testid="chart-monthly-cost">
          <h3 className="font-semibold mb-1 flex items-center gap-2"><Coins className="w-4 h-4 text-primary" /> {t("monthly_costs") || "Monthly Maintenance Costs"}</h3>
          <p className="text-xs text-muted-foreground mb-4">{t("last_6_months") || "Last 6 months"} · SAR</p>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={charts?.monthly_costs || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Line type="monotone" dataKey="cost" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border shadow-none" data-testid="chart-driver-performance">
          <h3 className="font-semibold mb-1 flex items-center gap-2"><UserCircle2 className="w-4 h-4 text-primary" /> {t("driver_performance") || "Driver Performance"}</h3>
          <p className="text-xs text-muted-foreground mb-4">{t("last_30_days") || "Last 30 days"} · {t("completed")} / {t("trips")}</p>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={charts?.driver_performance || []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="driver" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip />
                <Legend />
                <Bar dataKey="completed" fill="hsl(var(--primary))" name={t("completed")} />
                <Bar dataKey="total" fill="hsl(var(--muted-foreground))" name={t("trips")} opacity={0.5} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
